// Smartsheet Mappings — the admin console for the ROLODEX sheet IDs. Repoint any sheet, add a whole
// season, or reset a sheet to its built-in default, all without touching code (edits take effect within
// ~60s, no deploy). Styled as a clean config console (Vercel/Supabase feel): grouped cards, monospace
// IDs, copy, "custom vs default" badges. The "?" in the header explains how to create a season.
// "Loading ↔ PDI items" keeps the aliases that let a loading item match a PDI row worded differently.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoadingPdiAdminData, LoadingPdiAdminItem, LoadingPdiAliases } from '@technohull/shared';
import { ErrorState, InlineError, LoadingState, Modal, PageHeader, SyncLabel, useConfirm } from '../../components/ui.js';
import { IconChevronRight, IconClose } from '../../components/icons.js';
import { useIsCompact } from '../../lib/media.js';
import { AdminBack } from './AdminHub.js';
import {
  useAdminMappings, useSaveMappingSheet, useDeleteMappingSheet, useAddSeason, useDeleteSeason,
  useLoadingPdiAdmin, useSaveLoadingPdiAliases,
  type MappingsData,
} from '../../features/admin.js';

const FAMILIES = ['40-50', 'GT7-GTX-38', '45-47-XPD'] as const;
const ID_RE = /^\d{6,20}$/;
// sheet IDs are long digit strings: the phone opens its number pad, and Enter is "done", not a new line
const ID_INPUT = { inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'off', enterKeyHint: 'done' } as const;

function nextSeasonId(seasons: { id: string }[]): string {
  const starts = seasons.map((s) => parseInt(s.id, 10)).filter((n) => !Number.isNaN(n));
  const last = starts.length ? Math.max(...starts) : 25;
  const pad = (n: number) => String(n % 100).padStart(2, '0');
  return `${pad(last + 1)}-${pad(last + 2)}`;
}

/** one editable sheet-ID row: monospace field + copy, with Save/Cancel in Copy's place when dirty and Reset
 *  when it overrides a default. Enter saves a dirty, valid ID. */
function IdRow({ label, value, def, placeholder, onSave, onReset, busy }: {
  label: string;
  value: string;
  def?: string;                 // the built-in default, if any (enables the "custom" badge + Reset)
  placeholder?: string;         // shown while blank, e.g. the baseline ID an inherited row falls back to
  onSave: (id: string) => void;
  onReset?: () => void;
  busy?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const trimmed = draft.trim();
  const dirty = trimmed !== value;
  const custom = def != null && value !== def;
  const valid = ID_RE.test(trimmed);
  const [copied, setCopied] = useState(false);
  // writeText rejects asynchronously (blocked permission / unfocused page), so handle it on the promise.
  const onCopy = () => {
    void navigator.clipboard?.writeText(value).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => { /* clipboard blocked — ignore */ },
    );
  };
  return (
    <div className="map-row">
      <span className="map-row-label">{label}</span>
      <input
        className="field map-id"
        {...ID_INPUT}
        value={draft}
        spellCheck={false}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && dirty && valid && !busy) onSave(trimmed); }}
        aria-label={`${label} sheet ID`}
      />
      {custom && !dirty && <span className="map-badge custom" title="Overrides the built-in default">custom</span>}
      {dirty && !valid && <span className="map-badge warn" title="A Smartsheet sheet ID is 16-18 digits">check ID</span>}
      <span className="map-row-actions">
        {/* nothing to copy from a blank (inherited) row; while editing, Save/Cancel take Copy's place */}
        {!dirty && value && <button className="btn btn-sm btn-ghost" aria-label={`Copy ${label} ID`} title="Copy ID" onClick={onCopy}>{copied ? 'Copied' : 'Copy'}</button>}
        {dirty && <button className="btn btn-sm btn-primary" disabled={!valid || busy} onClick={() => onSave(trimmed)}>Save</button>}
        {dirty && <button className="btn btn-sm btn-ghost" onClick={() => setDraft(value)}>Cancel</button>}
        {!dirty && custom && onReset && <button className="btn btn-sm btn-ghost" disabled={busy} title="Revert to the built-in default" onClick={onReset}>Reset</button>}
      </span>
    </div>
  );
}

/** one season's sheets. Below the desktop floor each season folds (the current one starts open) so the
 *  page isn't three near-identical stacks of IDs; at desktop widths every season is open, as before. */
function SeasonCard({ id, current, remove, children }: { id: string; current: boolean; remove: ReactNode; children: ReactNode }) {
  const compact = useIsCompact();
  const [open, setOpen] = useState(current);
  return (
    <details className="map-season" open={!compact || open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      {/* desktop: the header stays a plain, inert heading row (no fold, no extra tab stop) */}
      <summary className="map-season-hd" tabIndex={compact ? undefined : -1} onClick={compact ? undefined : (e) => e.preventDefault()}>
        <span className="map-season-id">{id}</span>
        {current && <span className="map-badge cur">current</span>}
        {remove}
        <IconChevronRight size={16} strokeWidth={2.2} className="map-season-chev" />
      </summary>
      {children}
    </details>
  );
}

function ColumnsTable({ title, cols }: { title: string; cols: Record<string, number> }) {
  const entries = Object.entries(cols);
  if (!entries.length) return null;
  return (
    <details className="map-cols">
      <summary>{title} <span className="muted">({entries.length} columns)</span></summary>
      <table className="tbl map-cols-tbl">
        <tbody>
          {entries.map(([field, id]) => (
            <tr key={field}><td>{field}</td><td className="mono muted">{id}</td></tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function NewSeasonModal({ data, onClose }: { data: MappingsData; onClose: () => void }) {
  const add = useAddSeason();
  const [id, setId] = useState(() => nextSeasonId(data.seasons));
  const [overview, setOverview] = useState('');
  const [prod, setProd] = useState<Record<string, string>>({ '40-50': '', 'GT7-GTX-38': '', '45-47-XPD': '' });
  // the PDI model groups come from the shared baseline. Each input is OPTIONAL — a blank one inherits.
  const pdiGroups = data.pdiSheets;
  const [pdi, setPdi] = useState<Record<string, string>>({});
  const idOk = /^\d{2}-\d{2}$/.test(id.trim());
  const dup = data.seasons.some((s) => s.id === id.trim());
  const allIds = ID_RE.test(overview.trim()) && FAMILIES.every((f) => ID_RE.test((prod[f] ?? '').trim()));
  // optional PDI ids: fine to be blank, but a filled one must be a valid sheet id.
  const pdiOk = pdiGroups.every((g) => { const v = (pdi[g.key] ?? '').trim(); return v === '' || ID_RE.test(v); });
  const canSave = idOk && !dup && allIds && pdiOk && !add.isPending;
  // flag a filled-in but malformed sheet ID, like IdRow's "check ID", so a disabled Save explains itself.
  const badId = (v: string) => !!v.trim() && !ID_RE.test(v.trim());
  const checkId = <span className="map-badge warn" style={{ display: 'inline-block', marginTop: 4 }} title="A Smartsheet sheet ID is 16-18 digits">check ID</span>;
  const submit = () => {
    if (!canSave) return;
    const pdiOut: Record<string, string> = {};
    for (const g of pdiGroups) { const v = (pdi[g.key] ?? '').trim(); if (v) pdiOut[g.key] = v; }
    add.mutate(
      {
        id: id.trim(),
        overview: overview.trim(),
        prod: Object.fromEntries(FAMILIES.map((f) => [f, prod[f]!.trim()])),
        ...(Object.keys(pdiOut).length ? { pdi: pdiOut } : {}),
      },
      { onSuccess: onClose },
    );
  };
  return (
    <Modal
      title="New season"
      width={520}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={add.isPending}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={submit}>{add.isPending ? 'Adding…' : 'Add season'}</button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        A season maps to four Smartsheet sheets: one Overview and three Production sheets. Copy last season's
        sheets in Smartsheet (keep the same column titles), then paste their IDs here.
      </p>
      <label className="map-flabel">Season
        <input className="field map-id" value={id} aria-invalid={(!!id.trim() && !idOk) || undefined} onChange={(e) => setId(e.target.value)} placeholder="27-28" />
        {id.trim() && !idOk && <span className="map-badge warn" style={{ display: 'inline-block', marginTop: 4 }}>use YY-YY</span>}
      </label>
      {dup && <div className="map-badge warn" style={{ marginBottom: 8 }}>that season already exists</div>}
      <label className="map-flabel">Overview sheet ID
        <input className="field map-id" {...ID_INPUT} value={overview} spellCheck={false} aria-invalid={badId(overview) || undefined} onChange={(e) => setOverview(e.target.value)} />
        {badId(overview) && checkId}
      </label>
      {FAMILIES.map((f) => (
        <label className="map-flabel" key={f}>Production · {f}
          <input className="field map-id" {...ID_INPUT} value={prod[f] ?? ''} spellCheck={false} aria-invalid={badId(prod[f] ?? '') || undefined} onChange={(e) => setProd((p) => ({ ...p, [f]: e.target.value }))} />
          {badId(prod[f] ?? '') && checkId}
        </label>
      ))}
      {pdiGroups.length > 0 && (
        <>
          <p className="muted" style={{ marginBottom: 4, fontSize: 13 }}>
            PDI sheets (optional) - leave a group blank to reuse the shared baseline sheet for this season.
          </p>
          {pdiGroups.map((g) => (
            <label className="map-flabel" key={g.key}>PDI · {g.label}
              <input className="field map-id" {...ID_INPUT} value={pdi[g.key] ?? ''} spellCheck={false} placeholder={`inherits ${g.id}`}
                aria-invalid={badId(pdi[g.key] ?? '') || undefined}
                onChange={(e) => setPdi((p) => ({ ...p, [g.key]: e.target.value }))} />
              {badId(pdi[g.key] ?? '') && checkId}
            </label>
          ))}
        </>
      )}
      {add.isError && <InlineError error={add.error} onDismiss={() => add.reset()} />}
    </Modal>
  );
}

/** the alias map as saved: every item that has aliases, by its exact name. */
function aliasMap(data: LoadingPdiAdminData): LoadingPdiAliases {
  const out: LoadingPdiAliases = {};
  for (const it of data.items) if (it.aliases.length) out[it.name] = [...it.aliases];
  return out;
}

/** one loading item: its number and name, its aliases (each removable), "Add alias" (which opens a field
 *  that suggests the PDI wordings no item matches yet), and how many boats it matched in the PDI sheets. */
function LoadingPdiRow({ item, busy, onAdd, onRemove }: {
  item: LoadingPdiAdminItem;
  busy: boolean;
  onAdd: (alias: string, done: () => void) => void;
  onRemove: (alias: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const text = draft.trim();
  const close = () => { setAdding(false); setDraft(''); };
  const add = () => { if (text && !busy) onAdd(text, close); };
  return (
    <li className="lp-row">
      <span className="lp-name"><span className="lp-no">{item.itemNo + 1}</span>{item.name}</span>
      <span className="lp-count" title="Boats whose PDI block has a row for this item">
        <span className={`pill ${item.boats ? 'ok' : ''}`}>{item.boats} {item.boats === 1 ? 'boat' : 'boats'}</span>
        {item.ambiguousBoats > 0 && (
          <span className="pill warn" title="These boats have two PDI rows that match this item; the first one is used">
            {item.ambiguousBoats} with two rows
          </span>
        )}
      </span>
      <div className="lp-aliases">
        {item.aliases.map((a) => (
          <span key={a} className="lp-alias">
            <span className="lp-alias-txt">{a}</span>
            <button type="button" className="lp-alias-x" aria-label={`Remove alias ${a}`} title="Remove" disabled={busy} onClick={() => onRemove(a)}>
              <IconClose size={12} />
            </button>
          </span>
        ))}
        {adding ? (
          <span className="lp-add">
            <input
              className="field"
              list="lp-candidates"
              value={draft}
              placeholder="PDI wording…"
              aria-label={`New PDI alias for ${item.name}`}
              autoComplete="off"
              enterKeyHint="done"
              autoFocus
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); add(); }
                else if (e.key === 'Escape') close();
              }}
            />
            <button type="button" className="btn btn-sm btn-primary" disabled={!text || busy} onClick={add}>{busy ? 'Saving…' : 'Add'}</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={close}>Cancel</button>
          </span>
        ) : (
          <button type="button" className="btn btn-sm btn-ghost lp-add-btn" disabled={busy} onClick={() => setAdding(true)}>Add alias</button>
        )}
      </div>
    </li>
  );
}

/** Mappings › "Loading ↔ PDI items". It loads on its own, so the sheet IDs above still show when the PDI
 *  sheets can't be read. Every add or remove saves the whole alias map. */
function LoadingPdiSection() {
  const { data, isLoading, error, refetch, isFetching } = useLoadingPdiAdmin();
  const save = useSaveLoadingPdiAliases();
  const busy = save.isPending;
  const change = (item: LoadingPdiAdminItem, list: string[], done?: () => void) => {
    if (!data) return;
    const next = aliasMap(data);
    if (list.length) next[item.name] = list;
    else delete next[item.name];
    save.mutate({ aliases: next }, { onSuccess: () => done?.() });
  };
  const unmatched = data?.candidates ?? [];
  return (
    <div className="map-section">
      <div className="map-section-hd">
        <div>
          <h3>Loading ↔ PDI items</h3>
          <span className="muted">A loading item matches the PDI row with the same name; case, accents and punctuation don't matter. Add the PDI wording as an alias when it differs.</span>
        </div>
        {data && <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Counting…' : 'Recount'}</button>}
      </div>
      {isLoading && <LoadingState label="Reading the PDI sheets…" />}
      {error && !data && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <>
          {data.pdiError
            ? <InlineError error={new Error(`The PDI sheets couldn't be read, so the counts show 0: ${data.pdiError}`)} />
            : <p className="lp-sum">Checked against {data.pdiBoats} {data.pdiBoats === 1 ? 'boat' : 'boats'} in the PDI sheets (this season and earlier ones).</p>}
          {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
          <datalist id="lp-candidates">
            {unmatched.map((c) => <option key={c.name} value={c.name}>{`${c.boats} ${c.boats === 1 ? 'boat' : 'boats'}`}</option>)}
          </datalist>
          <ol className="lp-list">
            {data.items.map((it) => (
              <LoadingPdiRow
                key={it.itemNo}
                item={it}
                busy={busy}
                onAdd={(alias, done) => change(it, [...it.aliases, alias], done)}
                onRemove={(alias) => change(it, it.aliases.filter((a) => a !== alias))}
              />
            ))}
          </ol>
          {unmatched.length > 0 && (
            <details className="map-cols lp-unmatched">
              <summary>PDI wordings that match no loading item <span className="muted">({unmatched.length})</span></summary>
              <ul className="lp-cands">
                {unmatched.map((c) => (
                  <li key={c.name}><span className="lp-cand-name">{c.name}</span><span className="muted">{c.boats} {c.boats === 1 ? 'boat' : 'boats'}</span></li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

export function AdminMappings() {
  const { data, isLoading, error, refetch, isFetching } = useAdminMappings();
  const saveSheet = useSaveMappingSheet();
  const delSheet = useDeleteMappingSheet();
  const delSeason = useDeleteSeason();
  const [confirmNode, confirm] = useConfirm();
  const [showNew, setShowNew] = useState(false);

  const busy = saveSheet.isPending || delSheet.isPending || delSeason.isPending;
  const defSeason = useMemo(() => new Map((data?.defaults.seasons ?? []).map((s) => [s.id, s])), [data]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const d = data.defaults;
  const isDefaultSeason = (id: string) => defSeason.has(id);

  return (
    <>
      <AdminBack />
      <PageHeader title="Smartsheet Mappings">
        <button className="btn btn-sm btn-primary" onClick={() => setShowNew(true)}>+ New season</button>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} idle="Refresh" busyText="Refreshing…" /></button>
      </PageHeader>
      <p className="muted" style={{ marginTop: -4 }}>
        The live sheet IDs the app reads. Edits take effect within about a minute, with no deploy. A blank or
        wrong ID safely falls back to the built-in default. Every change is recorded in the Audit log.
      </p>
      {(saveSheet.isError || delSheet.isError || delSeason.isError) && (
        <InlineError error={saveSheet.error ?? delSheet.error ?? delSeason.error} onDismiss={() => { saveSheet.reset(); delSheet.reset(); delSeason.reset(); }} />
      )}

      {/* ── Seasons ── */}
      <div className="map-section">
        <div className="map-section-hd">
          <div><h3>Seasons</h3><span className="muted">Overview + three Production sheets per season</span></div>
        </div>
        {data.seasons.map((s) => (
          <SeasonCard key={s.id} id={s.id} current={s.id === data.currentSeason}
            remove={!isDefaultSeason(s.id) && (
              <button className="btn btn-sm btn-danger map-season-del" disabled={busy}
                onClick={async (e) => {
                  e.preventDefault(); // a button in the summary acts on its own, never also folding the card
                  if (await confirm({ title: 'Remove season', message: `Remove season ${s.id}? Its sheets stay in Smartsheet; only the mapping is deleted.`, confirmLabel: 'Remove', danger: true })) delSeason.mutate({ season: s.id });
                }}>
                Remove season
              </button>
            )}>
            <IdRow label="Overview" value={s.overview} def={defSeason.get(s.id)?.overview} busy={busy}
              onSave={(id) => saveSheet.mutate({ key: `overview:${s.id}`, kind: 'overview', season: s.id, sheetId: id, label: `Overview ${s.id}` })}
              onReset={isDefaultSeason(s.id) ? () => delSheet.mutate({ key: `overview:${s.id}` }) : undefined} />
            {FAMILIES.map((f) => (
              <IdRow key={f} label={`Production · ${f}`} value={s.prod[f] ?? ''} def={defSeason.get(s.id)?.prod[f]} busy={busy}
                onSave={(id) => saveSheet.mutate({ key: `prod:${s.id}:${f}`, kind: 'production', season: s.id, family: f, sheetId: id, label: `Prod ${f} ${s.id}` })}
                onReset={isDefaultSeason(s.id) ? () => delSheet.mutate({ key: `prod:${s.id}:${f}` }) : undefined} />
            ))}
            {/* per-season PDI: each model group shows this season's effective sheet. A group left at the
                shared baseline reads "inherited"; overriding it writes a season-specific row. */}
            {data.pdiSheets.length > 0 && (
              <div className="map-season-pdi">
                <span className="map-subhd">PDI sheets <span className="muted">· blank inherits the shared baseline</span></span>
                {data.pdiSheets.map((base) => {
                  const eff = (data.pdiSheetsBySeason[s.id] ?? []).find((x) => x.key === base.key) ?? base;
                  const overridden = String(eff.id) !== String(base.id);
                  return (
                    <IdRow key={base.key} label={`PDI · ${base.label}`} value={overridden ? eff.id : ''} def="" placeholder={`inherits ${base.id}`} busy={busy}
                      onSave={(id) => saveSheet.mutate({ key: `pdi:${s.id}:${base.key}`, kind: 'pdi', season: s.id, sheetId: id, label: `PDI ${base.label} ${s.id}`, meta: { colStatus: base.colStatus, colComments: base.colComments } })}
                      onReset={overridden ? () => delSheet.mutate({ key: `pdi:${s.id}:${base.key}` }) : undefined} />
                  );
                })}
              </div>
            )}
          </SeasonCard>
        ))}
      </div>

      {/* ── Station sheets (season-independent) ── */}
      <div className="map-section">
        <div className="map-section-hd"><div><h3>Station sheets</h3><span className="muted">One sheet each, shared across seasons</span></div></div>
        <IdRow label="Service" value={data.generic.service} def={d.generic.service} busy={busy}
          onSave={(id) => saveSheet.mutate({ key: 'generic:service', kind: 'generic', sheetId: id, label: 'service' })}
          onReset={() => delSheet.mutate({ key: 'generic:service' })} />
        <IdRow label="Deck Flooring" value={data.generic.deck} def={d.generic.deck} busy={busy}
          onSave={(id) => saveSheet.mutate({ key: 'generic:deck', kind: 'generic', sheetId: id, label: 'deck' })}
          onReset={() => delSheet.mutate({ key: 'generic:deck' })} />
        <IdRow label="Loading" value={data.generic.loading} def={d.generic.loading} busy={busy}
          onSave={(id) => saveSheet.mutate({ key: 'generic:loading', kind: 'generic', sheetId: id, label: 'loading' })}
          onReset={() => delSheet.mutate({ key: 'generic:loading' })} />
        <IdRow label="GRP Inspection" value={data.grp.id} def={d.grp.id} busy={busy}
          onSave={(id) => saveSheet.mutate({ key: 'grp', kind: 'grp', sheetId: id, label: 'GRP Inspection' })}
          onReset={() => delSheet.mutate({ key: 'grp' })} />
        <IdRow label="Programming / DS" value={data.programming.id} def={d.programming.id} busy={busy}
          onSave={(id) => saveSheet.mutate({ key: 'programming', kind: 'programming', sheetId: id, label: 'Programming Overview' })}
          onReset={() => delSheet.mutate({ key: 'programming' })} />
      </div>

      {/* ── Shared PDI baseline ── */}
      <div className="map-section">
        <div className="map-section-hd"><div><h3>Shared PDI baseline</h3><span className="muted">One per model group. Any season inherits these unless it sets its own PDI above.</span></div></div>
        {data.pdiSheets.map((p) => {
          const dp = d.pdiSheets.find((x) => x.key === p.key);
          return (
            <IdRow key={p.key} label={p.label} value={p.id} def={dp?.id} busy={busy}
              onSave={(id) => saveSheet.mutate({ key: `pdi:${p.key}`, kind: 'pdi', sheetId: id, label: p.label, meta: { colStatus: p.colStatus, colComments: p.colComments } })}
              onReset={dp ? () => delSheet.mutate({ key: `pdi:${p.key}` }) : undefined} />
          );
        })}
      </div>

      {/* ── Loading ↔ PDI item names (aliases) ── */}
      <LoadingPdiSection />

      {/* ── Column IDs (read-only reference) ── */}
      <div className="map-section">
        <div className="map-section-hd"><div><h3>Column IDs</h3><span className="muted">Editable columns are matched by column title at runtime, so a new season rarely needs these.</span></div></div>
        <ColumnsTable title="Overview columns" cols={data.ovCols} />
        <ColumnsTable title="GRP columns" cols={data.grp.cols} />
        <ColumnsTable title="Programming columns" cols={data.programming.cols} />
      </div>

      {showNew && <NewSeasonModal data={data} onClose={() => setShowNew(false)} />}
      {confirmNode}
    </>
  );
}
